import { API_USER_URL } from "@/env";
import { Flex, GetProps, Input, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { mixPostV1 } from "services/mixin";

type OTPProps = GetProps<typeof Input.OTP>;
const VerifyOTP: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const uid = searchParams.get('uid');
    const reactive = searchParams.get('reactive');

    async function verifyOtp(otp: string, uid: string | null) {
        try {
            if (!uid) {
                message.error('User ID is missing!');
                return;
            }
            if (otp.length !== 6) {
                message.error('Please enter a valid 6-digit OTP code!');
                return;
            }
            if (!reactive) {
                const res = await fetch(API_USER_URL + '/api/v1/be/verify-email-otp', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: uid,
                        otp: otp
                    }),
                });
                const data = await res.json();
                if (data.status) {
                    message.success('OTP verified successfully!');
                    navigate('/sign-up/step');
                } else {
                    message.error(data.message || 'OTP verification failed, please try again!');
                }
            }
            else {
                const res = await fetch(API_USER_URL + '/api/v1/be/verify-email-otp', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: uid,
                        otp: otp
                    }),
                });
                const data = await res.json();
                if (data.status) {
                    message.success('OTP verified successfully!');
                    // localStorage.setItem('token', data.data.access_token);
                    // localStorage.setItem('user', JSON.stringify(data.data.info));
                    message.success('Account reactivated successfully!');
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    navigate('/');
                } else {
                    message.error(data.message || 'OTP verification failed, please try again!');
                }
            }
        }
        catch (error) {
            message.error('OTP verification failed, please try again!');
        }
    }

    async function resendOtp(uid: string | null) {
        try {
            if (!uid) {
                message.error('User ID is missing!');
                return;
            }
            const res = await fetch(API_USER_URL + '/api/v1/be/resend-verification-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: uid,
                }),
            });
            const data = await res.json();
            if (data.status) {
                message.success('OTP resent successfully! Please check your email.');
            } else {
                message.error(data.message || 'Failed to resend OTP, please try again!');
            }
        }
        catch (error) {
            message.error('Failed to resend OTP, please try again!');
        }
    }

    const [otpValue, setOtpValue] = useState('');
    useEffect(() => {
        if (otpValue.length === 6) {
            verifyOtp(otpValue, uid);
        }
    }, [otpValue]);
    const onChange: OTPProps['onChange'] = async (text) => {
        setOtpValue(text);
    };

    const onInput: OTPProps['onInput'] = (value) => {
        // change every input
    };

    const sharedProps: OTPProps = {
        onChange,
        onInput,
    };

    return (
        <div className="w-full min-h-screen flex flex-col auth-wrapper">
            <div className=" w-full h-screen flex flex-row justify-center items-center px-4 py-6 overflow-hidden">
                <div className="relative col-xl-5 col-lg-6 ms-auto px-sm-4 align-self-center py-4 d-none d-lg-block">
                    <img src="/images/login_bg.svg" style={{
                        transform: 'translateY(-50%)',
                        top: '50%',
                        transformOrigin: 'center',
                        transformBox: 'fill-box',
                    }} alt="Background" className="absolute w-3/4" />
                </div>
                <div className="w-full max-w-xl mx-auto px-4 py-4">
                    <ToastContainer />
                    <div className="max-w-[500px] mx-auto card card-body" style={{
                        boxShadow: '0 0.5rem 1rem rgba(2, 2, 76, 0.05)',
                        border: 'none',
                        padding: '2.25rem',
                        borderRadius: '1rem',
                    }}>
                        <div className="text-center mb-4 bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
                            <h5 className="mb-1 text-lg font-semibold">{t('auth.verify_otp_title')}</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{t('auth.verify_otp_desc')}</p>
                        </div>
                        <div>
                            <Flex justify="center" align="center" gap={16}>
                                <Input.OTP
                                    {...sharedProps}
                                    value={otpValue}
                                    onChange={(value) => setOtpValue(value)}
                                    length={6}
                                    size="large"
                                    autoFocus
                                />
                            </Flex>
                        </div>
                        <div className="text-center mt-4">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                {t('auth.didnt_receive_code')}{' '}
                                <button
                                    onClick={() => resendOtp(uid)}
                                    className="text-primary hover:underline"
                                >
                                    {t('auth.resend_otp')}
                                </button>
                            </p>
                        </div>
                        <div className="text-center mt-4">
                            <button
                                onClick={() => verifyOtp(otpValue, uid)}
                                className="w-full bg-primary hover:bg-primary/90 !text-white font-semibold py-2 px-4 rounded"
                            >
                                {t('auth.verify_otp')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOTP;